import { alpha, type Theme } from '@material-ui/core/styles';

/**
 * The cockpit reports what was observed, so its palette encodes confidence
 * instead of category: a single ink, stepped by opacity. Steps stay at least
 * 0.12 apart so they remain distinguishable side by side at rail height.
 *
 * Absence is deliberately not a step on this ramp. An unobserved interval is
 * hatched, never filled, because a pale fill reads as a small measured value —
 * the one thing this product promises never to imply.
 */
const CONFIDENCE_ALPHA = {
  evidence: 0.84,
  covered: 0.7,
  running: 0.55,
  detected: 0.4,
  inferred: 0.28,
  wait: 0.15,
} as const;

export type CockpitConfidenceStep = keyof typeof CONFIDENCE_ALPHA;

export function confidenceInk(theme: Theme, step: CockpitConfidenceStep) {
  return alpha(theme.palette.text.primary, CONFIDENCE_ALPHA[step]);
}

/** Reads as "not measured" rather than "measured and small". */
export function absenceHatch(theme: Theme) {
  const line = alpha(theme.palette.text.primary, 0.2);
  return {
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `repeating-linear-gradient(135deg, ${line} 0 1.5px, transparent 1.5px 6px)`,
  };
}

/**
 * The cockpit's own type tiers. The theme's scale packs every size the cockpit
 * used into a 0.25rem band, which cannot express hierarchy; these five tiers
 * are spaced far enough apart to be read as ranks.
 */
export const cockpitType = {
  /** Measured values. Tabular figures keep columns of numbers aligned. */
  numeral: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  /** Labels the tier below it; never carries content of its own. */
  eyebrow: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '0.09em',
    textTransform: 'uppercase' as const,
  },
} as const;
